import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Stack, Skeleton } from '@mui/material';
import GrainIcon from '@mui/icons-material/Grain';
import {  ChevronRight as ChevronRightIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

const CarbonCreditCard = ({ siteId }) => {
  const { accessToken } = useAuth();
  const [salinityData, setSalinityData] = useState({
    salinity: { value: null, change: null, changeValue: null },
    loading: true,
    error: null,
  });

  const navigate = useNavigate();

  const baseURL = process.env.REACT_APP_BASEURL;

  const handleMoreClick = () => {
    navigate("/dashboard/statistics", {
      state: { siteId: siteId !== "all" ? siteId : null },
    });
  };

  useEffect(() => {
    const fetchSalinityData = async () => {
      setSalinityData(prev => ({ ...prev, loading: true, error: null }));
      try {
        // Fetch devices based on siteId
        const deviceUrl =
          siteId === "all"
            ? `${baseURL}/devices`
            : `${baseURL}/devices/?site=${siteId}`;
        const devicesRes = await axios.get(deviceUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const deviceIds = devicesRes.data.map(d => d.id).join(',');

        if (!deviceIds) {
          setSalinityData({
            salinity: { value: 'N/A', change: 'N/A', changeValue: null },
            loading: false,
            error: null,
          });
          return;
        }

        // Fetch aggregated measurements for current period (last 7 days)
        const endDate = new Date().toISOString();
        const startDate = new Date(
          new Date(endDate).setDate(new Date(endDate).getDate() - 7)
        ).toISOString();
        const aggRes = await axios.get(`${baseURL}/measurements/aggregated`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            device: deviceIds,
            since: startDate,
            until: endDate,
            window_size: 1,
            aggregate_all: true,
            fields: 'salinity',
          },
        });

        const data = aggRes.data[0] || {};

        // Calculate salinity data similar to SoilHealthCard moisture calculation
        const salinityMean = data.salinity?.mean || null;
        const salinityMin = data.salinity?.min || null;
        const salinityChange = salinityMean && salinityMin
          ? (salinityMean - salinityMin).toFixed(1)
          : null;

        setSalinityData({
          salinity: {
            value: salinityMean ? `${salinityMean.toFixed(1)} dS/m` : 'N/A',
            change: salinityChange,
            changeValue: salinityChange,
            minValue: salinityMin ? `${salinityMin.toFixed(2)} dS/m` : 'N/A',
          },
          loading: false,
        });
      } catch (error) {
        console.error('Salinity data fetch error:', error.response ? error.response.data : error.message);
        setSalinityData(prev => ({ ...prev, loading: false, error: 'Failed to load salinity data' }));
      }
    };
    fetchSalinityData();
  }, [accessToken, siteId, baseURL]);

  const { salinity, loading, error } = salinityData;

  if (loading) return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 15,
        p: 2,
        background: "linear-gradient(to right, #0288d1, #1976d2)",
        color: "#fff",
        boxShadow: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Skeleton 
            variant="text" 
            width={150} 
            height={64} 
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
          />
        </Box>
        <Skeleton 
          variant="circular" 
          width={40} 
          height={40} 
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
        />
      </Stack>

      <Skeleton 
        variant="text" 
        width={200} 
        height={80} 
        sx={{ 
          my: 2,
          ml: 2,
          bgcolor: 'rgba(255, 255, 255, 0.1)' 
        }} 
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton 
          variant="text" 
          width={100} 
          height={16} 
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
        />
        <Skeleton 
          variant="rectangular" 
          width={150} 
          height={36} 
          sx={{ 
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.1)' 
          }} 
        />
      </Stack>
    </Card>
  );

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: 15,
        p: 3,
        background: "linear-gradient(to right, #0288d1, #1976d2)",
        color: "#fff",
        boxShadow: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4" fontWeight="bold" color="#fff">
          Soil Salinity
        </Typography>
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <GrainIcon />
        </Box>
      </Stack>

      <Typography variant="h3" fontWeight="bold" mb={2} mt={1}>
        {salinity.value}
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", ml: 0, paddingRight: 2 }}>
          {salinity.changeValue !== null &&
            (salinity.changeValue > 0 ? (
              <ArrowUpIcon sx={{ fontSize: 16, mr: 0.5, color: "red" }} />
            ) : salinity.changeValue < 0 ? (
              <ArrowDownIcon sx={{ fontSize: 16, mr: 0.5, color: "green" }} />
            ) : null)}
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {salinity.change} dS/m (from Min Value:{salinity.minValue})
          </Typography>
        </Box>
      </Stack>
      <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 0.5,
            cursor: "pointer",
          }}
          onClick={handleMoreClick}
        >
          <Typography variant="body2">More</Typography>
          <ChevronRightIcon fontSize="small" />
      </Box>
    </Card>
  );
};

export default CarbonCreditCard;